import React, { PureComponent } from 'react'
import { HeaderProps } from '../index'
import Measure, { ContentRect } from 'react-measure'
import KeywordSearch from '../common/KeywordSearch'
import Cart from '../common/Cart'
import PrimaryNav from './common/PrimaryNav'
import SecondaryNav from './common/SecondaryNav'
import ExploreMenu from './common/ExploreMenu'
import MobileTertiaryNav from './mobile/MobileTertiaryNav'
import { UserAgentConsumer } from '#src/containers/UserAgent'
import StickyHeader from '#src/components/StickyHeader'
import { DepartmentConsumer } from '#src/context/department'
import isSupportedBrowser from '#src/services/unsupportedBrowsers'
import { throttle } from 'lodash'
import { NavDepartmentsComponent, NavHierarchyComponent } from '#src/generated/graphql'
import classnames from 'classnames'
import styles from './styles.css'

interface HeaderState {
  scrollTop: number
  collapsed: boolean
  primaryNavHeight?: number
  exploreMenuOpen: boolean
  browseMenuOpen: boolean
  searchOpen: boolean
}

// How much the user must scroll in order for it to "count" as a scroll event
const MINIMUM_SCROLL_PIXELS_DELTA = 5

class Header extends PureComponent<HeaderProps, HeaderState> {
  public throttledHandleWindowScrolled: EventListener
  private shouldCollapse: boolean

  public constructor(props: HeaderProps) {
    super(props)

    this.state = {
      scrollTop: 0,
      collapsed: false,
      exploreMenuOpen: false,
      browseMenuOpen: false,
      searchOpen: false
    }

    this.shouldCollapse = !!props.collapsable || !!props.stickyHeaderAfterScroll

    this.handlePrimaryNavResized = this.handlePrimaryNavResized.bind(this)
    this.handleWindowScrolled = this.handleWindowScrolled.bind(this)
    this.throttledHandleWindowScrolled = throttle(this.handleWindowScrolled, 200)
    this.handleExploreOpened = this.handleExploreOpened.bind(this)
    this.handleBrowseOpened = this.handleBrowseOpened.bind(this)
    this.handleSearchOpened = this.handleSearchOpened.bind(this)
    this.handleExploreClosed = this.handleExploreClosed.bind(this)
    this.handleBrowseClosed = this.handleBrowseClosed.bind(this)
    this.handleSearchClosed = this.handleSearchClosed.bind(this)
  }

  public componentDidMount() {
    this.setState({
      scrollTop: window.scrollY
    })

    if (this.shouldCollapse) {
      window.addEventListener('scroll', this.throttledHandleWindowScrolled)
    }
  }

  public componentDidUpdate(prevProps: HeaderProps) {
    if (this.shouldCollapse && !prevProps.collapsable) {
      window.addEventListener('scroll', this.throttledHandleWindowScrolled)
    }

    if (prevProps.collapsable && !this.shouldCollapse) {
      window.removeEventListener('scroll', this.throttledHandleWindowScrolled)
    }
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.throttledHandleWindowScrolled)
  }

  /**
   * Collapse the nav when the user scrolls down, and reveal it again when they begin to scroll up.
   *
   * See: https://codepen.io/egoens/pen/NxejgJ
   */
  public handleWindowScrolled() {
    const currentScrollTop = window.scrollY
    const previousScrollTop = this.state.scrollTop
    const headerHeight = parseInt(
      this.props.hideSecondaryNav ? styles.HEADER_HEIGHT_SMALL : styles.HEADER_HEIGHT,
      10
    )
    const documentHeight = Math.max(
      document.body.scrollHeight,
      document.documentElement.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.offsetHeight,
      document.body.clientHeight,
      document.documentElement.clientHeight
    )

    // Make sure they scroll more than delta
    if (Math.abs(previousScrollTop - currentScrollTop) <= MINIMUM_SCROLL_PIXELS_DELTA) {
      return
    }

    // If they scrolled down and are past the navbar, collapse the navbar.
    // This is necessary so you never see what is "behind" the navbar.
    if (currentScrollTop > previousScrollTop && currentScrollTop > headerHeight) {
      // Scroll Down
      this.setState({
        collapsed: true
      })
    } else if (!this.props.stickyHeaderAfterScroll && currentScrollTop < documentHeight) {
      // Scroll Up
      this.setState({
        collapsed: false
      })
    } else if (this.props.stickyHeaderAfterScroll && currentScrollTop < headerHeight) {
      // Scroll Up only when you hit the top of the document
      this.setState({
        collapsed: false
      })
    }

    this.setState({
      scrollTop: currentScrollTop
    })
  }

  public handlePrimaryNavResized(rect: ContentRect) {
    if (rect.bounds) {
      this.setState({
        primaryNavHeight: rect.bounds.height
      })
    }
  }

  public handleExploreOpened() {
    this.setState({ exploreMenuOpen: true })
  }

  public handleBrowseOpened() {
    this.setState({ browseMenuOpen: true })
  }

  public handleSearchOpened() {
    this.setState({ searchOpen: true })
  }

  public handleExploreClosed() {
    this.setState({ exploreMenuOpen: false })
  }

  public handleBrowseClosed() {
    this.setState({ browseMenuOpen: false })
  }

  public handleSearchClosed() {
    this.setState({ searchOpen: false })
  }

  public render() {
    const { collapsed, primaryNavHeight, exploreMenuOpen, browseMenuOpen, searchOpen } = this.state
    const {
      className,
      collapsable,
      stickyHeader,
      hideSecondaryNav: propsHideSecondaryNav,
      transparent,
      replaceSearchWithPath,
      stickyHeaderAfterScroll,
      location
    } = this.props
    //  When hidden, we shift the header so the primary nav is off-screen
    const offsetWhenHidden = -1 * (primaryNavHeight || 0)
    const offsetTop = collapsable && collapsed ? offsetWhenHidden : 0
    const hasOpenPanel =
      exploreMenuOpen || browseMenuOpen || searchOpen || location.hash === '#cart'

    const hideSecondaryNav = stickyHeader !== undefined || propsHideSecondaryNav

    return (
      <DepartmentConsumer>
        {({ department: activeDepartmentContext }) => {
          return (
            <NavDepartmentsComponent>
              {({ data }) => {
                const departments = data && data.departmentsForNavigation

                return (
                  <NavHierarchyComponent
                    // @ts-ignore `graphql-code-generator` incorrectly types `variables` as required
                    //   See: https://github.com/dotansimha/graphql-code-generator/issues/1897
                    variables={
                      activeDepartmentContext
                        ? {
                            activeDepartmentId: activeDepartmentContext.id
                          }
                        : undefined
                    }
                    skip={!activeDepartmentContext}
                  >
                    {({ data }) => {
                      const activeDepartment = data && data.activeDepartment

                      return (
                        <UserAgentConsumer>
                          {userAgent => {
                            const isBrowserSupported = isSupportedBrowser(userAgent)

                            return (
                              <>
                                {((stickyHeader && !stickyHeaderAfterScroll) ||
                                  (stickyHeaderAfterScroll && collapsed)) && (
                                  <StickyHeader>{stickyHeader}</StickyHeader>
                                )}
                                <div
                                  className={classnames(styles.wrapper, className, {
                                    [styles.wrapperHeight]:
                                      !(stickyHeader && !stickyHeaderAfterScroll) &&
                                      !propsHideSecondaryNav,
                                    [styles.wrapperHeightSmall]:
                                      !(stickyHeader && !stickyHeaderAfterScroll) &&
                                      propsHideSecondaryNav,
                                    [styles.wrapperLifted]: hasOpenPanel,
                                    [styles.wrapperHidden]: transparent
                                  })}
                                >
                                  <header
                                    style={{ transform: `translateY(${offsetTop}px)` }}
                                    className={classnames(styles.header, {
                                      [styles.unsupportedHeader]: !isBrowserSupported,
                                      [styles.collapsed]: collapsed,
                                      [styles.transparent]: transparent
                                    })}
                                  >
                                    <Measure bounds={true} onResize={this.handlePrimaryNavResized}>
                                      {({ measureRef }) => (
                                        <PrimaryNav
                                          department={activeDepartment}
                                          measureRef={measureRef}
                                          onExploreClicked={this.handleExploreOpened}
                                          transparent={transparent}
                                        />
                                      )}
                                    </Measure>

                                    {!hideSecondaryNav && (
                                      <SecondaryNav
                                        departments={departments}
                                        activeDepartment={activeDepartment}
                                        onBrowseClicked={this.handleBrowseOpened}
                                        onSearchClicked={this.handleSearchOpened}
                                        transparent={transparent}
                                        replaceSearchWithPath={replaceSearchWithPath}
                                      />
                                    )}
                                  </header>

                                  <ExploreMenu
                                    open={exploreMenuOpen}
                                    onClose={this.handleExploreClosed}
                                    departments={departments || null}
                                    activeDepartment={activeDepartment}
                                  />

                                  {activeDepartment && (
                                    <MobileTertiaryNav
                                      open={browseMenuOpen}
                                      items={activeDepartment.navigation.topLevelItems}
                                      onClose={this.handleBrowseClosed}
                                    />
                                  )}
                                  <KeywordSearch
                                    open={searchOpen}
                                    department={activeDepartment}
                                    onClose={this.handleSearchClosed}
                                  />

                                  <Cart />
                                </div>
                              </>
                            )
                          }}
                        </UserAgentConsumer>
                      )
                    }}
                  </NavHierarchyComponent>
                )
              }}
            </NavDepartmentsComponent>
          )
        }}
      </DepartmentConsumer>
    )
  }
}

export default Header
